export function latLngToXY(
  lat: number,
  lng: number,
  centerLat: number,
  centerLng: number
): { x: number; y: number } {
  const R = 6371000;

  const latRad = lat * (Math.PI / 180);
  const lngRad = lng * (Math.PI / 180);
  const centerLatRad = centerLat * (Math.PI / 180);
  const centerLngRad = centerLng * (Math.PI / 180);

  const x = R * (lngRad - centerLngRad) * Math.cos((latRad + centerLatRad) / 2);
  const y = R * (latRad - centerLatRad);

  return { x, y };
}

export function xyToLatLng(
  x: number,
  y: number,
  centerLat: number,
  centerLng: number
): { lat: number; lng: number } {
  const R = 6371000;

  const centerLatRad = centerLat * (Math.PI / 180);
  const centerLngRad = centerLng * (Math.PI / 180);

  const latRad = y / R + centerLatRad;

  const avgLat = (latRad + centerLatRad) / 2;
  const lngRad = x / (R * Math.cos(avgLat)) + centerLngRad;

  return {
    lat: latRad * (180 / Math.PI),
    lng: lngRad * (180 / Math.PI),
  };
}

function lineSegmentIntersection(
  lineStart: { x: number; y: number },
  direction: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number }
): { x: number; y: number } | null {
  const x1 = p1.x;
  const y1 = p1.y;
  const x2 = p2.x;
  const y2 = p2.y;

  const x3 = lineStart.x;
  const y3 = lineStart.y;
  const x4 = lineStart.x + direction.x;
  const y4 = lineStart.y + direction.y;

  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (Math.abs(denom) < 1e-9) {
    return null;
  }

  const t1 = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  // Intersection parameter t2 on lineStart->lineStart+direction
  const t2 = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

  if (t1 < 0 || t1 > 1 || t2 < 0) {
    return null;
  }

  // Intersection point
  const interX = x1 + t1 * (x2 - x1);
  const interY = y1 + t1 * (y2 - y1);

  return { x: interX, y: interY };
}

/**
 * find the intersection point of a ray from `center` at `angle` with a polygon.
 *
 * @param center   The center lat/lng
 * @param polygon  An array of lat/lng vertices, e.g. [ { lat, lng }, { lat, lng }, ... ]
 * @param angleDeg Angle in degrees, measured from North=0°, going clockwise.
 */
export function getIntersectionPoint(
  center: { lat: number; lng: number },
  polygon: Array<{ lat: number; lng: number }>,
  angleDeg: number
): { lat: number; lng: number } | null {
  if (polygon.length < 2) {
    return null; // Not a valid polygon
  }

  // Convert center to (x0, y0)
  const centerXY = latLngToXY(center.lat, center.lng, center.lat, center.lng);

  //  Convert polygon to local XY
  const polygonXY = polygon.map((pt) =>
    latLngToXY(pt.lat, pt.lng, center.lat, center.lng)
  );

  // Convert the angle from "North=0°, clockwise" to standard math "X-axis=0, counter-clockwise".
  const thetaRad = ((90 - angleDeg) * Math.PI) / 180;

  // Create a direction vector from that angle (unit vector).
  const direction = {
    x: Math.cos(thetaRad),
    y: Math.sin(thetaRad),
  };

  let closestPoint: { x: number; y: number } | null = null;
  let minDist = Number.POSITIVE_INFINITY;

  // Loop through polygon edges and find intersections with the ray
  for (let i = 0; i < polygonXY.length; i++) {
    const p1 = polygonXY[i];
    const p2 = polygonXY[(i + 1) % polygonXY.length]; // wrap around to form closed polygon

    const inter = lineSegmentIntersection(centerXY, direction, p1, p2);
    if (inter) {
      const dx = inter.x - centerXY.x;
      const dy = inter.y - centerXY.y;
      const distSq = dx * dx + dy * dy;
      if (distSq < minDist) {
        minDist = distSq;
        closestPoint = inter;
      }
    }
  }

  // 6) If we found at least one intersection, convert the closest one back to lat/lng
  if (closestPoint) {
    return xyToLatLng(closestPoint.x, closestPoint.y, center.lat, center.lng);
  }

  // If no intersection was found (which could happen if the polygon doesn't enclose center), return null
  return null;
}

/**
 * Finds intersection points for multiple angle pairs.
 *
 * @param center    Center lat/lng
 * @param polygon   Array of { lat, lng } forming the polygon boundary
 * @param anglePairs  Array of pairs [angleA, angleB]
 * @returns  Array of pairs [
 *    { lat, lng } | null,  // intersection for angleA
 *    { lat, lng } | null   // intersection for angleB
 * ]
 */
export function intersectionPointsFromAnglePairs(
  center: { lat: number; lng: number },
  polygon: Array<{ lat: number; lng: number }>,
  anglePairs: Array<[number, number]>
): Array<
  [{ lat: number; lng: number } | null, { lat: number; lng: number } | null]
> {
  return anglePairs.map(([angleA, angleB]) => {
    const pA = getIntersectionPoint(center, polygon, angleA);
    const pB = getIntersectionPoint(center, polygon, angleB);
    return [pA, pB];
  });
}

export function lineSegmentIntersection2D(
  s1p1: { x: number; y: number },
  s1p2: { x: number; y: number },
  s2p1: { x: number; y: number },
  s2p2: { x: number; y: number }
): { x: number; y: number } | null {
  const x1 = s1p1.x,
    y1 = s1p1.y;
  const x2 = s1p2.x,
    y2 = s1p2.y;
  const x3 = s2p1.x,
    y3 = s2p1.y;
  const x4 = s2p2.x,
    y4 = s2p2.y;

  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

  // If denom is zero (or extremely close), lines are parallel or coincident
  if (Math.abs(denom) < 1e-12) {
    return null;
  }

  // These are the parametric "fraction" along each segment where intersection occurs
  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
    return null;
  }

  const ix = x1 + ua * (x2 - x1);
  const iy = y1 + ua * (y2 - y1);
  return { x: ix, y: iy };
}

/**
 * Calculates the intersection point of a ray from `center` at `angleDeg`
 * with a polygon defined by an array of {x, y} points.
 *
 * @param center  The center point { x, y }.
 * @param polygon An array of { x, y } vertices (assumed to be closed).
 * @param angleDeg Angle in degrees (measured from North, clockwise).
 * @returns The intersection point { x, y } or null.
 */
export function getIntersectionPointXY(
  center: { x: number; y: number },
  polygon: Array<{ x: number; y: number }>,
  angleDeg: number
): { x: number; y: number } | null {
  if (polygon.length < 2) return null;
  // Convert angle from “North = 0° (clockwise)” to math standard (0° = East)
  const thetaRad = ((90 - angleDeg) * Math.PI) / 180;
  const direction = { x: Math.cos(thetaRad), y: Math.sin(thetaRad) };
  // Extend the ray far away
  const rayEnd = {
    x: center.x + direction.x * 1e6,
    y: center.y + direction.y * 1e6,
  };

  let closest: { x: number; y: number } | null = null;
  let minDistSq = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const p1 = polygon[i];
    const p2 = polygon[(i + 1) % polygon.length];
    const inter = lineSegmentIntersection2D(center, rayEnd, p1, p2);
    if (inter) {
      const dx = inter.x - center.x;
      const dy = inter.y - center.y;
      const distSq = dx * dx + dy * dy;
      if (distSq < minDistSq) {
        minDistSq = distSq;
        closest = inter;
      }
    }
  }
  return closest;
}

/**
 * For each pair of angles, finds the two intersection points
 * (using getIntersectionPointXY) and returns an array of pairs.
 */
export function intersectionPointsFromAnglePairsXY(
  center: { x: number; y: number },
  polygon: Array<{ x: number; y: number }>,
  anglePairs: Array<[number, number]>
): Array<[{ x: number; y: number } | null, { x: number; y: number } | null]> {
  return anglePairs.map(([angleA, angleB]) => {
    const pA = getIntersectionPointXY(center, polygon, angleA);
    const pB = getIntersectionPointXY(center, polygon, angleB);
    return [pA, pB];
  });
}

/**
 * Given two line segments (A1-A2 and B1-B2), computes their intersection.
 * This is the same as your original 2D line-segment intersection function.
 */
// function lineSegmentIntersection2D(
//   A1: { x: number; y: number },
//   A2: { x: number; y: number },
//   B1: { x: number; y: number },
//   B2: { x: number; y: number }
// ): { x: number; y: number } | null {
//   const x1 = A1.x,
//     y1 = A1.y;
//   const x2 = A2.x,
//     y2 = A2.y;
//   const x3 = B1.x,
//     y3 = B1.y;
//   const x4 = B2.x,
//     y4 = B2.y;

//   const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
//   if (Math.abs(denom) < 1e-12) return null;

//   const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
//   const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

//   if (ua < 0 || ua > 1 || ub < 0 || ub > 1) return null;

//   return {
//     x: x1 + ua * (x2 - x1),
//     y: y1 + ua * (y2 - y1),
//   };
// }
